"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { Button, buttonVariants } from "@/components/ui/button"
import { ChevronRight, Search } from "lucide-react"

type SectionType = 'product' | 'collection' | 'tool' | 'blog'

// Curated top collections — every slug verified live in the collections table
// (07/20/2026), so the 404 never links a shopper into another dead end.
const TOP_COLLECTIONS: Array<{ slug: string; label: string }> = [
  { slug: 'refrigerators', label: 'Refrigerators' },
  { slug: 'french-door-refrigerators', label: 'French Door' },
  { slug: 'mini-fridges', label: 'Mini Fridges' },
  { slug: 'freezers', label: 'Freezers' },
  { slug: 'wine-coolers', label: 'Wine Coolers' },
  { slug: 'ice-makers', label: 'Ice Makers' },
]

interface NotFoundStateProps {
  section: SectionType
}

const sectionConfig = {
  product: {
    headline: "Product Not Found",
    subtext: "We couldn't find the product you were looking for. Browse our refrigerator collection to find your perfect match.",
    ctaText: "Browse Refrigerators",
    ctaLink: "/collections/refrigerators"
  },
  collection: {
    headline: "Collection Not Found", 
    subtext: "This collection doesn't exist or may have moved. Explore all our appliance collections to find what you're looking for.",
    ctaText: "View All Collections",
    ctaLink: "/collections/all"
  },
  tool: {
    headline: "Tool Not Found",
    subtext: "We couldn't find the tool you're looking for. Check out our helpful calculators and guides for refrigerators and freezers.",
    ctaText: "Browse All Tools",
    ctaLink: "/tools"
  },
  blog: {
    headline: "Article Not Found",
    subtext: "This article doesn't exist or may have been moved. Explore our latest news and guides about refrigerators and freezers.",
    ctaText: "Read Our Blog",
    ctaLink: "/blogs/news"
  }
}

/**
 * Log 404 error to Supabase for analytics
 */
async function log404(url: string, section: SectionType, referrer: string | null) {
  try {
    // Get hostname to distinguish between environments
    // e.g., 'fridge.com', 'your-project.vercel.app', 'localhost:3000'
    const hostname = typeof window !== 'undefined' ? window.location.host : null

    await fetch('/api/log-404', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url,
        section,
        hostname,
        referrer,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
      })
    })
  } catch (error) {
    // Silently fail - don't disrupt user experience for logging
    // Silently handle - 404 logging is optional
  }
}

export function NotFoundState({ section }: NotFoundStateProps) {
  const config = sectionConfig[section]
  const lastLoggedUrl = useRef<string | null>(null)
  const router = useRouter()
  const [q, setQ] = useState("")

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const term = q.trim()
    if (term.length >= 2) router.push(`/search?q=${encodeURIComponent(term)}`)
  }

  // Log 404 on mount - use window.location.pathname for accurate URL
  useEffect(() => {
    // Get actual browser URL (more reliable than usePathname which can be cached)
    const currentUrl = typeof window !== 'undefined' ? window.location.pathname : '/'
    
    // Only log if we haven't already logged this exact URL
    if (lastLoggedUrl.current === currentUrl) return
    lastLoggedUrl.current = currentUrl

    const referrer = typeof document !== 'undefined' ? document.referrer : null
    log404(currentUrl, section, referrer)
  }, [section])

  return (
    <div className="py-12 sm:py-16 md:py-20">
      <div className="text-center max-w-lg mx-auto px-4">
        {/* Illustration */}
        <div className="mb-6 sm:mb-8">
          <Image
            src="/images/empty-collection-illustration.jpg"
            alt="Content not found | Fridge.com"
            width={200}
            height={200}
            className="mx-auto w-36 h-36 sm:w-44 sm:h-44 md:w-52 md:h-52 object-contain"
            priority
          />
        </div>

        {/* Headline */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-5">
          {config.headline}
        </h2>

        {/* Subtext */}
        <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed mb-8 sm:mb-10">
          {config.subtext}
        </p>

        {/* Search — the fastest way back to something real */}
        <form onSubmit={onSearch} className="mb-6 sm:mb-8" role="search">
          <label htmlFor="nf-search" className="sr-only">Search Fridge.com</label>
          <div className="flex items-center gap-2 max-w-md mx-auto rounded-full border border-gray-300 bg-white pl-4 pr-1.5 py-1.5 focus-within:border-[#0469f0] transition-colors">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <input
              id="nf-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search refrigerators, freezers, brands…"
              className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none"
            />
            <Button
              type="submit"
              variant="ctaPrimary"
              className="h-8 px-4 text-xs font-semibold rounded-full flex-shrink-0"
            >
              Search
            </Button>
          </div>
        </form>

        {/* CTA Button */}
        <Link href={config.ctaLink} className={buttonVariants({ variant: "ctaPrimary", className: "h-11 sm:h-12 px-6 sm:px-8 text-sm sm:text-base font-semibold" })}>
          {config.ctaText}
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />
        </Link>

        {/* Top collections — all slugs verified live */}
        <div className="mt-8 sm:mt-10">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Popular collections
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {TOP_COLLECTIONS.map((c) => (
              <Link
                key={c.slug}
                href={`/collections/${c.slug}`}
                className="px-3.5 py-1.5 rounded-full border border-gray-200 bg-white text-sm text-gray-700 hover:border-[#0469f0]/40 hover:text-[#0469f0] transition-colors"
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Identity line */}
        <p className="mt-8 text-xs text-gray-500 leading-relaxed max-w-sm mx-auto">
          Fridge.com is an independent refrigerator search engine — we compare
          real prices across major online retailers. We never sell products and
          there&rsquo;s no checkout here.
        </p>
      </div>
    </div>
  )
}
